import { useQueryClient } from "@tanstack/react-query";

import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { useSavePatientBaseline } from "features/patients/hooks/useSavePatientBaseline";

import { mapReviewToBaselineFields } from "../utils/reviewBaselineMapping";

/**
 * Keeps the patient's Baseline Details in sync with the latest CDM/OCF/PP
 * review. Returns a `syncBaseline(reviewData)` callback that the send hooks call
 * from their success path (covers both new submissions and corrections).
 *
 * The upsert is best-effort and non-blocking: it never throws and any failure is
 * only logged, so it can never break the primary review submission/UX.
 */
export const useSyncPatientBaselineFromReview = ({ patientId }) => {
  const queryClient = useQueryClient();
  const { data: baseline } = usePatientBaseline(patientId);
  const saveBaseline = useSavePatientBaseline();

  const syncBaseline = async (reviewData) => {
    try {
      const reviewFields = mapReviewToBaselineFields(reviewData);

      if (!Object.keys(reviewFields).length) return;

      await saveBaseline.mutateAsync({
        id: baseline?.id || null,
        patientId: Number(patientId),
        heightCm: baseline?.heightCm ?? null,
        weightKg: baseline?.weightKg ?? null,
        waistCm: baseline?.waistCm ?? null,
        bloodPressure: baseline?.bloodPressure ?? null,
        ...reviewFields,
      });

      queryClient.invalidateQueries({ queryKey: ["patientBaseline"] });
    } catch (error) {
      // Non-blocking: the review save is the primary action. useSavePatientBaseline
      // already sets meta.skipGlobalError; just log so it never surfaces to the GP.
      console.error("Failed to sync patient baseline from review:", error);
    }
  };

  return { syncBaseline };
};
